import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PaginationControlsProps } from './types'
import { Typogaphy } from 'vnetwork-ui'

export default function PaginationControls({
    currentPage,
    totalPages,
    pageSize,
    totalRecords,
    onPageChange,
    onPageSizeChange,
}: PaginationControlsProps) {
    const pageSizeOptions = [10, 20, 50]
    const startRecord = (currentPage - 1) * pageSize + 1
    const endRecord = Math.min(currentPage * pageSize, totalRecords)

    /** Generate visible page numbers with ellipsis */
    const getPageNumbers = () => {
        if (totalPages <= 5)
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        const pages: (number | '...')[] = [1]
        if (currentPage > 3) pages.push('...')
        const start = Math.max(2, currentPage - 1)
        const end = Math.min(totalPages - 1, currentPage + 1)
        for (let i = start; i <= end; i++) pages.push(i)
        if (currentPage < totalPages - 2) pages.push('...')
        pages.push(totalPages)
        return pages
    }

    return (
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            {/* Page size */}
            <div className="flex items-center gap-1.5">
                {pageSizeOptions.map((size) => (
                    <button
                        key={size}
                        onClick={() => onPageSizeChange(size)}
                        className={cn(
                            'h-7 px-2 rounded text-xs font-medium transition-colors',
                            pageSize === size
                                ? 'bg-primary/10 text-primary'
                                : 'text-gray-500 hover:bg-gray-100'
                        )}
                    >
                        {size}
                    </button>
                ))}
            </div>

            {/* Info + Navigation */}
            <div className="flex items-center gap-3">
                <Typogaphy className="text-xs text-gray-400">
                    {startRecord}–{endRecord} / {totalRecords}
                </Typogaphy>

                <div className="flex items-center gap-0.5">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={cn(
                            'p-1 rounded hover:bg-gray-100 transition-colors',
                            currentPage === 1 &&
                                'opacity-30 pointer-events-none'
                        )}
                    >
                        <ChevronLeft className="w-3.5 h-3.5 text-gray-600" />
                    </button>

                    {getPageNumbers().map((page, idx) =>
                        page === '...' ? (
                            <span
                                key={`ellipsis-${idx}`}
                                className="px-1 text-xs text-gray-400"
                            >
                                …
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => onPageChange(page)}
                                className={cn(
                                    'min-w-[24px] h-6 px-1 rounded text-xs font-medium transition-colors',
                                    currentPage === page
                                        ? 'bg-primary text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                )}
                            >
                                {page}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={cn(
                            'p-1 rounded hover:bg-gray-100 transition-colors',
                            currentPage === totalPages &&
                                'opacity-30 pointer-events-none'
                        )}
                    >
                        <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                </div>
            </div>
        </div>
    )
}
